/**
 * Spec 039 P3: turns a raw `POST /quant/strategies/{slug}/preview` body into
 * typed meta, candles and overlay. Pure; no fetching.
 *
 * Observed on live echo-core 0.65.0 (findings P0): with `include_candles: false`
 * the `candles` key is absent (absent and `null` are treated alike), and
 * `overlay.rev` only grows (it is never used to order responses).
 */
import { ApiError } from '@/app/lib/apiError'
import { quantError } from '@/app/lib/quant/types'
import { type CandlePoint, type OverlayLevel, type OverlayLine, type OverlayMarker, type OverlaySession, type OverlayZone, type PreviewInputRange, type PreviewOverlay } from './types'

export interface PreviewMeta {
  instrument: string
  tf: string
  data_through: number
  input_range: PreviewInputRange
}

export interface NormalizedPreview {
  meta: PreviewMeta
  /** `null` when the answer carried no candles. */
  candles: CandlePoint[] | null
  overlay: PreviewOverlay
}

function unusable(): ApiError {
  return quantError(502, 'The preview answer was not usable.', 'preview_failed')
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function list<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function normalizeCandles(value: unknown[]): CandlePoint[] {
  const out: CandlePoint[] = []
  for (const item of value) {
    if (!isRecord(item)) continue
    const { time, open, high, low, close, volume } = item
    if (!finite(time) || !finite(open) || !finite(high) || !finite(low) || !finite(close)) continue
    out.push({ time, open, high, low, close, volume: finite(volume) ? volume : 0 })
  }
  return out
}

export function normalizePreviewResponse(raw: unknown, includeCandles: boolean): NormalizedPreview {
  if (!isRecord(raw)) throw unusable()
  const rawOverlay = raw.overlay
  if (!isRecord(rawOverlay)) throw unusable()
  if (!finite(raw.data_through)) throw unusable()

  let candles: CandlePoint[] | null = null
  if (Array.isArray(raw.candles)) candles = normalizeCandles(raw.candles)
  if (includeCandles && candles === null) throw unusable()

  const range = isRecord(raw.input_range) ? raw.input_range : {}
  const meta: PreviewMeta = {
    instrument: typeof raw.instrument === 'string' ? raw.instrument : '',
    tf: typeof raw.tf === 'string' ? raw.tf : '',
    data_through: raw.data_through,
    input_range: {
      from: typeof range.from === 'string' ? range.from : '',
      to: typeof range.to === 'string' ? range.to : '',
    },
  }

  const overlay: PreviewOverlay = {
    rev: finite(rawOverlay.rev) ? rawOverlay.rev : 0,
    legend: list<string>(rawOverlay.legend),
    stats: list<[string, number | string]>(rawOverlay.stats),
    lines: list<OverlayLine>(rawOverlay.lines),
    zones: list<OverlayZone>(rawOverlay.zones),
    levels: list<OverlayLevel>(rawOverlay.levels),
    markers: list<OverlayMarker>(rawOverlay.markers),
    sessions: list<OverlaySession>(rawOverlay.sessions),
  }
  return { meta, candles, overlay }
}
